import { useState, useRef, useEffect, useId } from 'react'
import { DayPicker } from 'react-day-picker'
import { format, parse, isValid } from 'date-fns'
import 'react-day-picker/style.css'

// DateTimePicker (Wave 47d) — replacement for native <input
// type="datetime-local"> on the studio's editorial dark surfaces.
//
// Wraps react-day-picker for the calendar and adds an HH:mm time
// stepper. Emits and accepts the same "yyyy-MM-ddTHH:mm" string format
// that datetime-local uses, so it's a drop-in swap downstream.
//
// Theme integrity: react-day-picker is themed via the
// .studio-datetimepicker class in studio.css, which overrides every
// rdp-* CSS variable to the studio palette. Don't import a different
// stylesheet — that would re-introduce the light-mode rendering this
// component exists to replace.

const ISO_DATETIME_LOCAL = "yyyy-MM-dd'T'HH:mm"

function parseValue(value) {
  if (!value) return null
  const parsed = parse(value, ISO_DATETIME_LOCAL, new Date())
  if (isValid(parsed)) return parsed
  // Fallback: accept full ISO strings too (e.g. server-generated).
  const fallback = new Date(value)
  return isValid(fallback) ? fallback : null
}

function formatValue(date) {
  return format(date, ISO_DATETIME_LOCAL)
}

function formatDisplay(date) {
  return format(date, "MMM d, yyyy '·' HH:mm")
}

export function DateTimePicker({
  value = '',
  onChange,
  ariaLabel = 'Pick date and time',
  className = '',
  disabled = false,
  minuteStep = 5,
  placeholder = 'Pick date & time',
}) {
  const date = parseValue(value)
  const [open, setOpen] = useState(false)
  const [hour, setHour] = useState(date ? date.getHours() : 9)
  const [minute, setMinute] = useState(
    date ? Math.floor(date.getMinutes() / minuteStep) * minuteStep : 0
  )
  const wrapperRef = useRef(null)
  const id = useId()

  // Keep internal hour/minute in sync if the controlled `value` shifts
  // beneath us (e.g. parent reset). Only re-sync when the popover is
  // closed so typing in the steppers doesn't get clobbered. We
  // intentionally key on `value` rather than `date` because `date` is a
  // new Date object on every render — that would cause this effect to
  // fire endlessly.
  useEffect(() => {
    if (open) return
    const next = parseValue(value)
    if (!next) return
    setHour(next.getHours())
    setMinute(Math.floor(next.getMinutes() / minuteStep) * minuteStep)
  }, [value, open, minuteStep])

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    function handleKey(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  function emit(nextDate, nextHour, nextMinute) {
    if (!nextDate) {
      onChange?.('')
      return
    }
    const merged = new Date(nextDate)
    merged.setHours(nextHour, nextMinute, 0, 0)
    onChange?.(formatValue(merged))
  }

  function handleSelect(selected) {
    if (!selected) {
      emit(null, hour, minute)
      return
    }
    emit(selected, hour, minute)
  }

  function handleHourChange(e) {
    const next = Math.max(0, Math.min(23, Number(e.target.value) || 0))
    setHour(next)
    if (date) emit(date, next, minute)
  }

  function handleMinuteChange(e) {
    const raw = Math.max(0, Math.min(59, Number(e.target.value) || 0))
    const next = Math.floor(raw / minuteStep) * minuteStep
    setMinute(next)
    if (date) emit(date, hour, next)
  }

  return (
    <div
      ref={wrapperRef}
      className={`studio-datetimepicker ${className}`.trim()}
      style={{ position: 'relative' }}
    >
      <button
        type="button"
        id={id}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="studio-datetimepicker-trigger"
      >
        <span>{date ? formatDisplay(date) : placeholder}</span>
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="Date and time picker"
          className="studio-datetimepicker-popover"
        >
          <DayPicker
            mode="single"
            selected={date || undefined}
            onSelect={handleSelect}
            weekStartsOn={1}
            showOutsideDays
          />
          <div className="studio-datetimepicker-time">
            <span>Time</span>
            <input
              type="number"
              min="0"
              max="23"
              value={hour}
              onChange={handleHourChange}
              aria-label="Hour"
            />
            <span aria-hidden="true">:</span>
            <input
              type="number"
              min="0"
              max="59"
              step={minuteStep}
              value={String(minute).padStart(2, '0')}
              onChange={handleMinuteChange}
              aria-label="Minute"
            />
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="studio-datetimepicker-done"
            >
              Done
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
